import { Address, Property } from "@/lib/types/marketsuite/datatypes/property";
import { formatCurrency } from "@/lib/utils/format";

/**
 * Formats a Property address into a display string.
 * Combines postal code (if present) with location in uppercase.
 *
 * @param address - The address object to format.
 * @returns Formatted address string or empty string if no address.
 */
export const addressToString = (address: Address) => {
	if (!address) return "";

	let addressLine = address.location?.toUpperCase();

	if (address.postal_code) {
		addressLine = address.postal_code + ", " + addressLine;
	}

	return addressLine;
};

/**
 * Formats a Property price into a display string, including the rent unit and frequency when applicable.
 * Rent listings without a frequency default to per month.
 *
 * @param property - The property to format the price for.
 * @param t - Translator for the `ms` namespace.
 * @returns Formatted price string or null if the property has no price.
 */
export const priceToString = (property: Pick<Partial<Property>, "handover" | "listing_type">, t: (key: string) => string) => {
	if (!property.handover?.price) return null;
	if (property.handover.price === -1) return t("property-card.price-not-available");

	const rentPriceFrequency = property.handover.rent_price_frequency ?? (property.listing_type?.startsWith("Rent") ? "per_month" : null);

	return [
		formatCurrency(property.handover.price),
		property.handover.rent_price_unit && t(`property-card.price-unit.${property.handover.rent_price_unit}`),
		rentPriceFrequency && t(`property-card.price-frequency.${rentPriceFrequency}`),
	]
		.filter(Boolean)
		.join(" ");
};
